import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { DataChangeRequestsProvider } from 'libs.data';
import { Spinner } from 'libs.nucleus';
import { AxonAccountContext } from '../../contexts/axon_account/axon_account.context';
import { OrgContext } from '../../contexts/org';
import { getApiInstance } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useNativeDcrAccess } from '../../hooks/use_native_dcr_access';
import { useSiteContext } from '../../hooks/use_site_context';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
/**
 * Supplies everything the shared data change request views need for the current study, and keeps
 * studies that predate the native experience on the classic external app.
 */
export const DataChangeRequestsWrapper = () => {
    const { studyId } = useParams();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const { org } = useContext(OrgContext);
    const { account } = useContext(AxonAccountContext);
    const { isUserPermitted } = useLumosAuth();
    const { isNativeDcrEnabled, isLoading } = useNativeDcrAccess();
    const canManage = isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_UPDATE);
    const value = useMemo(() => {
        if (!environment || !account) {
            return undefined;
        }
        return {
            apiInstance: getApiInstance(environment, account.locale),
            environmentId: String(environment.id),
            siteId: site._id,
            siteNumber: site.c_number,
            countryCode: site.c_country,
            studyName: environment.study?.name ?? environment.name,
            protocolNumber: org?.c_protocol_number,
            participantIdFormat: org?.c_format_spec_subject_id,
            userEmail: account.email,
            basePath: `/site/study/${studyId}/data-change-requests`,
            canCreate: canManage,
            canEdit: canManage,
            canComment: true,
        };
    }, [environment, account, site, org, studyId, canManage]);
    if (isLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    if (!isNativeDcrEnabled) {
        return _jsx(Navigate, { to: `/site/study/${studyId}/participants`, replace: true });
    }
    if (!value) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    return (_jsx(DataChangeRequestsProvider, { value: value, children: _jsx(Outlet, {}) }));
};
